import React from 'react';
import { InvoiceStatus } from '@/services/api/invoice';
import dayjs from 'dayjs';

interface Props {
  grandTotal: number;
  flaggedCount: number;
  status: InvoiceStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  onMarkReviewed: () => void;
  isMarkingReviewed: boolean;
}

const formatRupiah = (n: number): string =>
  'Rp ' + n.toLocaleString('id-ID');

const InvoiceTotalCard: React.FC<Props> = ({
  grandTotal,
  flaggedCount,
  status,
  reviewedBy,
  reviewedAt,
  onMarkReviewed,
  isMarkingReviewed,
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(String(grandTotal)).catch(() => {});
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            FMS Total (net)
          </p>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-gray-900">
              {formatRupiah(grandTotal)}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              title="Copy net total to clipboard"
              className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
            >
              <i className="ri-file-copy-line text-[16px]" />
            </button>
          </div>
        </div>

        <div className="flex flex-col items-end gap-3">
          {status === 'REVIEWED' ? (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-lg px-3 py-2 text-sm">
              <i className="ri-checkbox-circle-fill text-green-500" />
              <span className="font-medium">Reviewed</span>
              {reviewedBy && (
                <span className="text-green-600 text-xs">
                  by {reviewedBy}
                  {reviewedAt && (
                    <>
                      {' '}
                      · {dayjs(reviewedAt).format('DD MMM YYYY HH:mm')}
                    </>
                  )}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 text-gray-600 rounded-lg px-3 py-2 text-sm">
              <i className="ri-time-line" />
              <span className="font-medium">Pending</span>
            </div>
          )}

          {status === 'PENDING' && (
            <button
              type="button"
              onClick={onMarkReviewed}
              disabled={isMarkingReviewed}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isMarkingReviewed ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1" />
                  Marking…
                </>
              ) : (
                'Mark as Correct'
              )}
            </button>
          )}
        </div>
      </div>

      {flaggedCount > 0 && (
        <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          <i className="ri-error-warning-fill text-red-500 text-[18px]" />
          <span>
            <strong>{flaggedCount} vehicle{flaggedCount !== 1 ? 's' : ''} flagged</strong>
            {' '}— total may be incomplete.
          </span>
        </div>
      )}
    </div>
  );
};

export default InvoiceTotalCard;
